import React from 'react'
import { Navigate } from 'react-router-dom'

import { useApp } from '../../AppContext'
import { CompanyProvider } from './Context'
import CompanyHeader from './components/CompanyHeader'
import CompanyTable from './components/CompanyTable'
import SingleCompany from './SingleCompany'

function Company() {
  const { companyContext, permissionsLoaded, sessionError, user, getSession } = useApp()

  // Wait for /auth/session before choosing a view, so a single-company user
  // never flashes the multi-company table first.
  if (!permissionsLoaded) return null

  // A FAILED session is not an answer about how many companies you have, and
  // every branch below reads the count. Left to fall through, a backend blip
  // rendered the multi-company table with "0 item" and an empty body — visually
  // identical to "this holding genuinely has no companies", on the screen where
  // that distinction decides whether the user thinks their company vanished.
  // Bail out with something that says what actually happened instead.
  if (sessionError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50/50 p-6">
        <div className="max-w-md rounded-xl border border-error/200 bg-base-white p-6 text-center">
          <p className="text-base font-semibold text-gray-light/900">
            Company data could not be loaded
          </p>
          <p className="mt-2 text-sm text-gray-light/600">
            We could not reach the server. This does not mean your company is gone —
            try reloading in a moment.
          </p>
          <button
            type="button"
            onClick={() => getSession()}
            className="mt-5 rounded-lg bg-brand/600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand/700"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const scope = user?.role?.scope ?? 'tenant'
  const count = companyContext?.count
  const companies = companyContext?.companies ?? []

  // A tenant with no company has no UI entry point anymore (the "New company"
  // button is gone — base companies are injected server-side). Send them
  // straight to the enrollment wizard to create/finish their first company.
  // Internal/ops users are never redirected — they manage via the table.
  if (scope !== 'internal' && count === 0) {
    return <Navigate to="/register-company-info" replace />
  }

  // Single-company holdings (the common case) skip the table and land directly
  // on their company view. >1 (rare, privileged) or internal (any count) keeps
  // the table, which is membership-scoped and safe. Missing context still falls
  // through to the table — but that now only means a SUCCESSFUL session that
  // carried no company_context (an older BE), never a failed one.
  if (count === 1 && companies[0]) {
    return <SingleCompany company={companies[0]} />
  }

  return (
    <CompanyProvider>
      <div className="flex h-full w-full flex-col bg-gray-50/50">
        <div className="flex w-full flex-1 min-h-0 flex-col">
          <CompanyHeader />
          <CompanyTable />
        </div>
      </div>
    </CompanyProvider>
  )
}

export default Company
